import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiChevronLeft, FiChevronRight } = FiIcons;

const TestimonialsCarousel = ({ className = "" }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  const testimonials = [
    {
      quote: "Got my restaurant's BOP policy in 5 minutes online! No agent appointments, no hassle - just instant coverage.",
      author: "Emma Rodriguez",
      location: "San Diego",
      business: "Restaurant Owner",
      rating: 5
    },
    {
      quote: "As a contractor, I needed workers' comp fast. Got my quote, bought coverage, and downloaded my certificate - all in 10 minutes!",
      author: "Michael Chen",
      location: "Los Angeles",
      business: "Construction Contractor",
      rating: 5
    },
    {
      quote: "Switched from my old agent to Next Insurance online. Saved $300/year and got better coverage. Wish I'd done this sooner!",
      author: "Sarah Johnson",
      location: "San Francisco",
      business: "Cleaning Service Owner",
      rating: 5
    },
    {
      quote: "The online process was so simple. Got general liability for my consulting business in minutes. No more waiting for callbacks!",
      author: "David Martinez",
      location: "Sacramento",
      business: "Business Consultant",
      rating: 5
    },
    {
      quote: "Amazing experience! Filed a claim through the app and got paid within days. This is how insurance should work.",
      author: "Lisa Thompson",
      location: "Oakland",
      business: "Retail Store Owner",
      rating: 5
    },
    {
      quote: "Needed coverage for my food truck ASAP. Got quoted, approved, and insured in under 15 minutes. Incredible!",
      author: "Carlos Mendoza",
      location: "Long Beach",
      business: "Food Truck Owner",
      rating: 5
    },
    {
      quote: "The transparency is refreshing. No hidden fees, clear coverage details, and instant policy documents. Highly recommend!",
      author: "Jennifer Kim",
      location: "Fresno",
      business: "Beauty Salon Owner",
      rating: 5
    },
    {
      quote: "My landscaping business needed workers' comp urgently. Got it done online while having coffee. Technology at its best!",
      author: "Roberto Silva",
      location: "Anaheim",
      business: "Landscaping Contractor",
      rating: 5
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 2 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 2 : prevIndex - 1
    );
  };

  const goToSlide = (index) => {
    setCurrentIndex(index);
  };

  return (
    <section className={`py-20 bg-white ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header with Updated Image */}
        <div className="text-center mb-16">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-8"
          >
            <img 
              src="https://cdn1.sharemyimage.com/smi/2025/09/09/Screen-Shot-2025-09-08-at-10.31.00-PM.png" 
              alt="Real Stories from Online Customers" 
              className="mx-auto h-16 w-auto object-contain"
            />
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto"
          >
            See why thousands of California businesses choose our online insurance experience
          </motion.p>
        </div>

        {/* Carousel Container */}
        <div className="relative">
          {/* Navigation Arrows */}
          <button
            onClick={prevTestimonial}
            className="absolute left-0 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
            aria-label="Previous testimonials"
          >
            <SafeIcon icon={FiChevronLeft} className="h-6 w-6 text-gray-600" />
          </button>

          <button
            onClick={nextTestimonial}
            className="absolute right-0 top-1/2 transform -translate-y-1/2 z-10 bg-white shadow-lg rounded-full p-3 hover:bg-gray-50 transition-colors duration-200 border border-gray-200"
            aria-label="Next testimonials"
          >
            <SafeIcon icon={FiChevronRight} className="h-6 w-6 text-gray-600" />
          </button>

          {/* Testimonials Grid */}
          <div className="overflow-hidden px-12">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 300 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -300 }}
                transition={{ duration: 0.5, ease: "easeInOut" }}
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                {testimonials.slice(currentIndex, currentIndex + 2).map((testimonial, index) => (
                  <motion.div
                    key={`${currentIndex}-${index}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="bg-gradient-to-br from-gray-50 to-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
                  >
                    {/* Star Rating */}
                    <div className="flex mb-6">
                      {[...Array(testimonial.rating)].map((_, i) => (
                        <SafeIcon 
                          key={i} 
                          icon={FiStar} 
                          className="h-5 w-5 text-yellow-400 fill-current" 
                        />
                      ))}
                    </div>

                    {/* Quote */}
                    <blockquote className="text-lg text-gray-700 mb-6 leading-relaxed">
                      "{testimonial.quote}"
                    </blockquote>

                    {/* Author Info */}
                    <div className="flex items-center">
                      <div className="bg-primary-100 w-12 h-12 rounded-full flex items-center justify-center mr-4">
                        <span className="text-primary-600 font-bold text-lg">
                          {testimonial.author.charAt(0)}
                        </span>
                      </div>
                      <div>
                        <div className="font-semibold text-gray-900 text-lg">
                          {testimonial.author}
                        </div>
                        <div className="text-sm text-gray-600">
                          {testimonial.business} • {testimonial.location}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Dot Indicators */}
          <div className="flex justify-center mt-8 space-x-2">
            {Array.from({ length: Math.ceil(testimonials.length / 2) }).map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index * 2)}
                className={`w-3 h-3 rounded-full transition-all duration-200 ${
                  Math.floor(currentIndex / 2) === index
                    ? 'bg-primary-600 w-8'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to testimonial set ${index + 1}`}
              />
            ))}
          </div>
        </div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center mt-16"
        >
          <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border border-green-200">
            <div className="text-3xl font-bold text-green-600 mb-2">4.8/5</div>
            <div className="text-gray-600">Customer Rating</div>
          </div>
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-6 rounded-xl border border-blue-200">
            <div className="text-3xl font-bold text-blue-600 mb-2">10 Min</div>
            <div className="text-gray-600">Average Quote Time</div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-6 rounded-xl border border-purple-200">
            <div className="text-3xl font-bold text-purple-600 mb-2">600K+</div>
            <div className="text-gray-600">Happy Customers</div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TestimonialsCarousel;